import { FC, useEffect, useRef, useState } from 'react';
import {
	Box,
	Button,
	CardContent,
	CircularProgress,
	Container,
	IconButton,
	Paper,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DateTime } from 'luxon';
import queryString from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getMerchantId } from 'redux/reducers/auth/selectors';
import {
	clearDirectDebitVerifyResult,
	completeSetupRequest,
	getClientAccessRequest,
	getPaymentMethodsRequest,
	getServicesRequest,
	getSummaryRequest,
	verifyDirectDebitRequest,
} from 'redux/reducers/paymentMethods/reducer';
import {
	getDirectDebitVerifyLoading,
	getDirectDebitVerifyResult,
	getPaymentMethodSetupCompleting,
	getPaymentMethodsBlockedMessage,
	getPaymentMethodsSummary,
	getPaymentsBlockedMessage,
} from 'redux/reducers/paymentMethods/selectors';
import { Notice } from 'ui/Notice';
import { WarningAlert } from 'ui/WarningAlert';
import { TransactionHistory } from './TransactionHistory';
import { PaymentMethodsInfoText } from './PaymentMethodsInfoText';
import { PaymentMethodsTable } from './PaymentMethodsTable';
import { AddPaymentMethodDialog } from './AddPaymentMethodDialog';
import { MakePaymentDialog } from './MakePaymentDialog';
import { DirectDebit } from './DirectDebit';
import { BlockedTooltip, ClientAccessBanner } from './ClientAccess';

// Ids that come back in the query string from Airwallex (3-D Secure, TrueLayer) are used only when they look valid.
const SETUP_ID_PATTERN = /^[0-9a-fA-F-]{32,36}$/;
const CONSENT_ID_PATTERN = /^[A-Za-z0-9_-]{1,100}$/;

const firstParam = (value: string | (string | null)[] | null | undefined): string | null => {
	const raw = Array.isArray(value) ? value[0] : value;
	return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
};

const formatDate = (value: string) => {
	const date = DateTime.fromISO(value);
	return date.isValid ? date.toLocaleString(DateTime.DATE_MED) : value;
};

export const PaymentMethods: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const merchantId = useSelector(getMerchantId);
	const summary = useSelector(getPaymentMethodsSummary);
	const setupCompleting = useSelector(getPaymentMethodSetupCompleting);
	const ddVerifyLoading = useSelector(getDirectDebitVerifyLoading);
	const ddVerifyResult = useSelector(getDirectDebitVerifyResult);
	// operator restrictions: the page is read-only for the blocked scope
	const paymentMethodsBlocked = useSelector(getPaymentMethodsBlockedMessage);
	const paymentsBlocked = useSelector(getPaymentsBlockedMessage);

	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const [makePaymentOpen, setMakePaymentOpen] = useState(false);
	const [returnFromSetup, setReturnFromSetup] = useState(false);
	const handledReturnRef = useRef<string | null>(null);

	// the page data (and again after switching the merchant)
	useEffect(() => {
		dispatch(getClientAccessRequest());
		dispatch(getSummaryRequest());
		dispatch(getPaymentMethodsRequest());
		dispatch(getServicesRequest());
	}, [dispatch, merchantId]);

	// Return from a redirect: 3-D Secure of the card binding (?setup={id}) or TrueLayer of a Direct Debit mandate
	// (?consent_id=…, ?dd_setup=…). The parameters are removed from the address afterwards.
	useEffect(() => {
		const params = queryString.parse(search);
		const setupId = firstParam(params.setup);
		const consentId = firstParam(params.consent_id);
		const ddSetupId = firstParam(params.dd_setup);
		if (!setupId && !consentId && !ddSetupId) return;

		const key = `${setupId ?? ''}|${consentId ?? ''}|${ddSetupId ?? ''}`;
		if (handledReturnRef.current !== key) {
			handledReturnRef.current = key;

			if (setupId && SETUP_ID_PATTERN.test(setupId)) {
				// the server checks the binding with Airwallex itself; "processing" is polled every 2 s for up to 60 s
				setReturnFromSetup(true);
				dispatch(completeSetupRequest({ setupId, notify: true }));
			}

			const paymentConsentId = consentId && CONSENT_ID_PATTERN.test(consentId) ? consentId : null;
			const ddSetup = ddSetupId && SETUP_ID_PATTERN.test(ddSetupId) ? ddSetupId : null;
			if (paymentConsentId || ddSetup) {
				dispatch(verifyDirectDebitRequest({ paymentConsentId, setupId: ddSetup }));
			}
		}

		navigate(pathname, { replace: true });
	}, [search, pathname, navigate, dispatch]);

	useEffect(() => {
		if (!setupCompleting) setReturnFromSetup(false);
	}, [setupCompleting]);

	const openAddDialog = () => {
		if (paymentMethodsBlocked) return;
		setAddDialogOpen(true);
	};

	const renderDirectDebitVerifyResult = () => {
		if (ddVerifyLoading) {
			return (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
					<CircularProgress size={22} />
					<Typography>Checking your Direct Debit…</Typography>
				</Box>
			);
		}
		if (!ddVerifyResult) return null;

		const firstDebit =
			ddVerifyResult.status === 'active' && ddVerifyResult.firstDebitNotBefore
				? `The first Direct Debit will be collected from ${formatDate(ddVerifyResult.firstDebitNotBefore)}.`
				: null;
		const closeButton = (
			<IconButton
				size="small"
				aria-label="Close"
				onClick={() => dispatch(clearDirectDebitVerifyResult())}
				sx={{ position: 'absolute', top: 8, right: 8 }}
			>
				<CloseIcon fontSize="small" />
			</IconButton>
		);
		const wrapperSx = { position: 'relative', mx: 2, mt: 3, mb: 2 } as const;

		if (ddVerifyResult.status === 'failed') {
			return (
				<Box sx={wrapperSx}>
					<WarningAlert
						sx={{ mt: 0, width: '100%', boxSizing: 'border-box', p: '20px', pr: '52px' }}
					>
						{ddVerifyResult.message || 'The Direct Debit could not be set up. Please try again.'}
					</WarningAlert>
					{closeButton}
				</Box>
			);
		}
		return (
			<Box sx={wrapperSx}>
				<Notice sx={{ mt: 0, width: '100%', boxSizing: 'border-box', pr: '52px' }}>
					{ddVerifyResult.message}
					{firstDebit && (
						<>
							{ddVerifyResult.message ? ' ' : ''}
							{firstDebit}
						</>
					)}
				</Notice>
				{closeButton}
			</Box>
		);
	};

	return (
		<Container maxWidth="xl">
			<Paper
				sx={{
					margin: '0px 10px 10px 10px',
					maxWidth: 'calc(100vw - 20px)',
					overflowX: 'auto',
				}}
			>
				<Box sx={{ width: 'auto', minWidth: '900px' }}>
					<CardContent>
						<Typography
							variant="h5"
							color="primary"
							sx={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '16px' }}
						>
							Payment Methods
						</Typography>
						<ClientAccessBanner
							messages={[paymentMethodsBlocked, paymentsBlocked]}
							sx={{ mb: 1 }}
						/>
						<PaymentMethodsInfoText />
						<PaymentMethodsTable />
					</CardContent>
					{returnFromSetup && setupCompleting && (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
							<CircularProgress size={22} />
							<Typography>Checking your new payment method…</Typography>
						</Box>
					)}
					{renderDirectDebitVerifyResult()}
					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10px',
							m: 2,
							alignItems: 'center',
							justifyContent: 'flex-end',
						}}
					>
						<BlockedTooltip message={paymentMethodsBlocked}>
							<Button
								variant="contained"
								onClick={openAddDialog}
								disabled={!!paymentMethodsBlocked}
							>
								Add payment method
							</Button>
						</BlockedTooltip>
						{summary?.directDebitAvailable && <DirectDebit />}
						<BlockedTooltip message={paymentsBlocked}>
							<Button
								variant="contained"
								onClick={() => setMakePaymentOpen(true)}
								disabled={!!paymentsBlocked}
							>
								Make a payment
							</Button>
						</BlockedTooltip>
					</Box>
				</Box>
			</Paper>
			<TransactionHistory />

			<AddPaymentMethodDialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} />
			<MakePaymentDialog open={makePaymentOpen} onClose={() => setMakePaymentOpen(false)} />
		</Container>
	);
};
